import type {
  FederationRatings,
  UserPrivateProfile,
  UserPublicProfile,
} from "@/pages/profile/api";
import type { Language } from "@/shared/i18n/LanguageContext";

type ProfileData = UserPublicProfile | UserPrivateProfile;

type AchievementCategory = "livestream" | "games" | "moves" | "identity";

export type AchievementStatus = "unlocked" | "in_progress" | "locked";

export type AchievementView = {
  id: string;
  category: AchievementCategory;
  title: string;
  subtitle: string;
  current: number;
  target: number;
  status: AchievementStatus;
  progressPercent: number;
  mark: string;
};

type TierAchievementDefinition = {
  idPrefix: string;
  category: AchievementCategory;
  title: (target: number) => string;
  subtitle: (target: number) => string;
  current: (profile: ProfileData) => number;
};

type AchievementDraft = Omit<AchievementView, "current" | "progressPercent" | "status"> & {
  current: number;
  status?: AchievementStatus;
};

const TIER_STEP = 100;
const FUTURE_TIER_COUNT = 3;

const achievementCopy = {
  en: {
    identityTitle: "Identity Forged",
    identitySubtitle: "Built a public chess identity with visible profile signals",
    firstLockTitle: "First Lock",
    firstLockSubtitle: "Submitted the first livestream guess",
    livestreamLocksTitle: (target: number) => `${target} Livestream Locks`,
    livestreamLocksSubtitle: (target: number) => `Locked ${target} livestream replay positions`,
    sharpSignalsTitle: (target: number) => `${target} Sharp Signals`,
    sharpSignalsSubtitle: (target: number) => `Matched ${target} livestream target moves`,
    firstChallengeTitle: "First Challenge",
    firstChallengeSubtitle: "Started the first model game",
    gamesPlayedTitle: (target: number) => `${target} Games Played`,
    gamesPlayedSubtitle: (target: number) => `Entered ${target} model rooms`,
    movesProvidedTitle: (target: number) => `${target} Moves Provided`,
    movesProvidedSubtitle: (target: number) => `Fed ${target} human choices into the system`,
  },
  zh: {
    identityTitle: "身份已锻造",
    identitySubtitle: "完善了公开棋手身份，让主页有可见的棋力信号",
    firstLockTitle: "第一次锁定",
    firstLockSubtitle: "完成了第一次直播猜棋",
    livestreamLocksTitle: (target: number) => `${target} 次直播锁定`,
    livestreamLocksSubtitle: (target: number) => `累计锁定 ${target} 个直播复盘局面`,
    sharpSignalsTitle: (target: number) => `${target} 次精准信号`,
    sharpSignalsSubtitle: (target: number) => `累计猜中 ${target} 步直播目标走法`,
    firstChallengeTitle: "第一次挑战",
    firstChallengeSubtitle: "开始了第一盘模型对局",
    gamesPlayedTitle: (target: number) => `${target} 盘模型对局`,
    gamesPlayedSubtitle: (target: number) => `进入过 ${target} 个模型房间`,
    movesProvidedTitle: (target: number) => `${target} 步走法贡献`,
    movesProvidedSubtitle: (target: number) => `向系统提交过 ${target} 步人类选择`,
  },
} as const;

const totalLivestreamLocks = (profile: ProfileData) =>
  (profile.activity_summary.livestream_guessed_right ?? 0) +
  (profile.activity_summary.livestream_guessed_wrong ?? 0);

const hasFederationRating = (ratings: FederationRatings | null | undefined) =>
  Object.values(ratings ?? {}).some((value) => typeof value === "number" && value > 0);

const hasIdentitySignal = (profile: ProfileData) =>
  Boolean(
    profile.avatar_url ||
      profile.bio?.trim() ||
      profile.title?.trim() ||
      profile.china_title?.trim() ||
      (typeof profile.fide_rating === "number" && profile.fide_rating > 0) ||
      hasFederationRating(profile.federation_ratings),
  );

const formatTierMark = (target: number) => {
  if (target < 1000) {
    return String(target);
  }

  const value = target / 1000;
  return Number.isInteger(value) ? `${value}K` : `${value.toFixed(1)}K`;
};

const toAchievementView = (draft: AchievementDraft): AchievementView => {
  const current = Math.max(0, draft.current);
  const progressPercent = Math.min(100, Math.round((current / draft.target) * 100));
  const status: AchievementStatus =
    draft.status ??
    (current >= draft.target ? "unlocked" : current > 0 ? "in_progress" : "locked");

  return {
    ...draft,
    current,
    status,
    progressPercent,
  };
};

const createTierAchievements = (
  profile: ProfileData,
  definition: TierAchievementDefinition,
): AchievementView[] => {
  const current = Math.max(0, definition.current(profile));
  const unlockedTierCount = Math.floor(current / TIER_STEP);
  const nextTierTarget = (unlockedTierCount + 1) * TIER_STEP;
  const visibleTierCount = Math.max(1, unlockedTierCount + FUTURE_TIER_COUNT);

  return Array.from({ length: visibleTierCount }, (_, index) => {
    const target = (index + 1) * TIER_STEP;
    const status: AchievementStatus =
      current >= target ? "unlocked" : current > 0 && target === nextTierTarget ? "in_progress" : "locked";

    return toAchievementView({
      id: `${definition.idPrefix}_${target}`,
      category: definition.category,
      title: definition.title(target),
      subtitle: definition.subtitle(target),
      current,
      target,
      status,
      mark: formatTierMark(target),
    });
  });
};

function tierDefinitions(language: Language) {
  const labels = achievementCopy[language];
  const livestreamLocks: TierAchievementDefinition = {
    category: "livestream",
    idPrefix: "livestream_locks",
    title: labels.livestreamLocksTitle,
    subtitle: labels.livestreamLocksSubtitle,
    current: totalLivestreamLocks,
  };
  const sharpSignals: TierAchievementDefinition = {
    category: "livestream",
    idPrefix: "livestream_sharp_signals",
    title: labels.sharpSignalsTitle,
    subtitle: labels.sharpSignalsSubtitle,
    current: (profile) => profile.activity_summary.livestream_guessed_right ?? 0,
  };
  const gamesPlayed: TierAchievementDefinition = {
    category: "games",
    idPrefix: "games_played",
    title: labels.gamesPlayedTitle,
    subtitle: labels.gamesPlayedSubtitle,
    current: (profile) => profile.activity_summary.games_played,
  };
  const movesProvided: TierAchievementDefinition = {
    category: "moves",
    idPrefix: "moves_provided",
    title: labels.movesProvidedTitle,
    subtitle: labels.movesProvidedSubtitle,
    current: (profile) => profile.activity_summary.moves_provided,
  };

  return { livestreamLocks, sharpSignals, gamesPlayed, movesProvided };
}

export function achievementViews(profile: ProfileData, language: Language = "en"): AchievementView[] {
  const labels = achievementCopy[language];
  const tiers = tierDefinitions(language);

  return [
    toAchievementView({
      id: "identity_forged",
      category: "identity",
      title: labels.identityTitle,
      subtitle: labels.identitySubtitle,
      target: 1,
      mark: "ID",
      current: hasIdentitySignal(profile) ? 1 : 0,
    }),
    toAchievementView({
      id: "livestream_first_lock",
      category: "livestream",
      title: labels.firstLockTitle,
      subtitle: labels.firstLockSubtitle,
      target: 1,
      mark: "01",
      current: totalLivestreamLocks(profile),
    }),
    ...createTierAchievements(profile, tiers.livestreamLocks),
    ...createTierAchievements(profile, tiers.sharpSignals),
    toAchievementView({
      id: "games_first_challenge",
      category: "games",
      title: labels.firstChallengeTitle,
      subtitle: labels.firstChallengeSubtitle,
      target: 1,
      mark: "VS",
      current: profile.activity_summary.games_played,
    }),
    ...createTierAchievements(profile, tiers.gamesPlayed),
    ...createTierAchievements(profile, tiers.movesProvided),
  ];
}
